import { cloneDeep as _cloneDeep, difference, filter as _filter, find, forEach, has, includes, isEmpty, isObject, last, remove, union } from 'lodash-es';

/**
 * Native port of the v1 `changesTransformer` factory + the `transformType`
 * constant (account-v1/src/components/changes-transformer/transformer.js).
 * WS5 #10 Stage A inc 2u: replaces the `$injector.get('changesTransformer')` /
 * `.get('transformType')` grabs (contest-details now; the touchpoint
 * integration/messaging/styling sheets later — ALL 10 types are ported so the
 * later drains just import this).
 *
 * Verbatim notes:
 * - The v1 prototype plumbing (`inherits(parent, child)` + the
 *   prototype-replacement ordering quirks) does not change observable
 *   `transform()` dispatch — each concrete type resolves the same nearest own
 *   method as the ES `extends` chains below (verified against the v1 source
 *   line-by-line), so plain classes are a faithful port.
 * - `angular.extend` → `ngExtend` (Object.assign + the setHashKey $$hashKey
 *   restore/strip) and `angular.copy` → cloneDeep + a recursive $$hashKey strip:
 *   the 2u differential audit proved bare Object.assign/cloneDeep LEAK an
 *   ng-repeat-stamped `$$hashKey` into kind-'N' merges / the HomepageMixed
 *   copies where v1 stripped it — unreachable from contest-details (kind-'E'
 *   only) but reachable from the later v1-data-fed touchpoint drains.
 *   Lodash object-matcher `find` kept as lodash.
 * - Input `changes` are deep-diff change records (`kind` E/N/A/D, `path`,
 *   `lhs`/`rhs`, `item`) — the same shape native-diff.util produces.
 * - The v1 `IntegrationDataTransformer.transform` invoked
 *   `BookingEngineDataTransformer.superClass.transform` (= ArrayData's, since
 *   BookingEngine defines no own transform) — `super.transform()` here resolves
 *   identically.
 */
/** angular.extend semantics: shallow merge, then restore the destination's own `$$hashKey` or strip it (setHashKey). */
function ngExtend(dst: any, ...srcs: any[]): any {
  const had = Object.prototype.hasOwnProperty.call(dst, '$$hashKey');
  const saved = dst.$$hashKey;
  Object.assign(dst, ...srcs);
  if (had) {
    dst.$$hashKey = saved;
  } else {
    delete dst.$$hashKey;
  }
  return dst;
}

/** angular.copy's `$$hashKey` strip, applied recursively to a cloneDeep result. */
function stripHashKeys(obj: any): any {
  if (Array.isArray(obj)) {
    obj.forEach(stripHashKeys);
  } else if (isObject(obj)) {
    delete (obj as any).$$hashKey;
    forEach(obj as any, (v: any) => stripHashKeys(v));
  }
  return obj;
}

export const transformType = {
  FlatData: 1,
  ArrayData: 2,
  MultiLanguageData: 3,
  BookingEngineData: 4,
  BookingEngineIntegrationData: 5,
  NotificationData: 6,
  MixedData: 7,
  PhotoContestPageIntegration: 8,
  HomepageMixedData: 9,
  AccountData: 10,
} as const;

abstract class Transformer {
  changes: any;
  source: any;
  results: any[] = [];
  result: any = {};

  constructor(changes: any, source: any) {
    this.changes = changes;
    this.source = source;
  }

  create(): any {
    throw 'Not implemented';
  }
  transform(): any {
    throw 'Not implemented';
  }
}

class FlatDataTransformer extends Transformer {
  /** Maps property names from the source object to the api payload. */
  mapper: Array<{ source: string; destination: string }> = [];

  override transform(): any {
    forEach(this.changes, (change: any) => {
      if (change.kind !== 'E' && change.kind !== 'N') return;
      const sourceName = last(change.path) as string;
      const map = find(this.mapper, { source: sourceName });
      const fieldName = (map && map.destination) || sourceName;
      if (change.kind === 'E') {
        this.result[fieldName] = change.rhs;
      } else if (change.kind === 'N') {
        isObject(change.rhs) ? ngExtend(this.result, change.rhs) : (this.result[fieldName] = change.rhs);
      }
    });
    return this.result;
  }
}

class ArrayDataTransformer extends FlatDataTransformer {
  sourceKey = 'id';
  destinationKey = 'id';
  arrayNestingDepth = 0;

  override transform(): any {
    forEach(this.changes, (change: any) => {
      if (change.kind !== 'E' && change.kind !== 'N') return;

      const index = change.path[this.arrayNestingDepth];
      const sourceKeyValue = this.source[index][this.sourceKey];
      const data: any = {};
      data[this.destinationKey] = sourceKeyValue;

      const arrayData: any = find(this.results, data) || {};
      const notExist = isEmpty(arrayData);

      const sourceName = last(change.path) as string;
      const map = find(this.mapper, { source: sourceName });
      const fieldName = (map && map.destination) || sourceName;

      if (change.kind === 'E') {
        arrayData[fieldName] = change.rhs;
      } else if (change.kind === 'N') {
        isObject(change.rhs) ? ngExtend(arrayData, change.rhs) : (arrayData[fieldName] = change.rhs);
      }

      if (notExist) {
        arrayData[this.destinationKey] = sourceKeyValue;
        forEach(this.mapper, (m: any) => {
          arrayData[m.destination] = arrayData[m.destination] || this.source[index][m.source];
        });
        this.results.push(arrayData);
      }
    });
    return this.results;
  }
}

class MultiLanguageDataTransformer extends ArrayDataTransformer {
  override sourceKey = 'code';
  override destinationKey = 'languageCode';
}

class BookingEngineDataTransformer extends ArrayDataTransformer {
  override sourceKey = 'id';
  override destinationKey = 'bookingEngineUUID';
}

class NotificationDataTransformer extends ArrayDataTransformer {
  override sourceKey = 'type';
  override destinationKey = 'type';
  constructor(changes: any, source: any) {
    super(changes, source);
    this.mapper.push({ source: 'userUUIDs', destination: 'userUUIDs' });
  }
}

class IntegrationDataTransformer extends BookingEngineDataTransformer {
  constructor(changes: any, source: any) {
    super(changes, source);
    this.mapper.push({ source: 'type', destination: 'bookingEngineTypeID' });
  }

  override transform(): any {
    super.transform();
    forEach(this.changes, (change: any) => {
      if (!includes(change.path, 'codes')) return;

      const index = change.path[0];
      const sourceKeyValue = this.source[index][this.sourceKey];
      const filter: any = {};
      filter[this.destinationKey] = sourceKeyValue;

      const arrayData: any = find(this.results, filter) || {};
      const notExist = isEmpty(arrayData);
      arrayData.codes = arrayData.codes || { newCodes: [], deleteCodes: [] };

      if (change.kind === 'E' && last(change.path) === 'code') {
        arrayData.codes.deleteCodes.push(change.lhs);
        arrayData.codes.newCodes.push(change.rhs);
      } else if (change.item && change.item.kind === 'N') {
        arrayData.codes.newCodes.push(change.item.rhs.code);
      } else if (change.item && change.item.kind === 'D') {
        arrayData.codes.deleteCodes.push(change.item.lhs.code);
      }

      const codeDiffs = union(
        difference(arrayData.codes.deleteCodes, arrayData.codes.newCodes),
        difference(arrayData.codes.newCodes, arrayData.codes.deleteCodes),
      );
      remove(arrayData.codes.deleteCodes, (code: any) => !includes(codeDiffs, code));
      remove(arrayData.codes.newCodes, (code: any) => !includes(codeDiffs, code));

      if (notExist) {
        arrayData[this.destinationKey] = sourceKeyValue;
        forEach(this.mapper, (m: any) => {
          arrayData[m.destination] = arrayData[m.destination] || this.source[index][m.source];
        });
        this.results.push(arrayData);
      }
    });
    return this.results;
  }
}

class MixedDataTransformer extends MultiLanguageDataTransformer {
  constructor(changes: any, source: any) {
    super(changes, source);
    this.arrayNestingDepth = 2;
  }

  override transform(): any {
    const originalSource = this.source;
    const originalChanges = this.changes;
    this.source = originalSource.languages.item;
    this.changes = _filter(originalChanges, (change: any) => change.path[0] === 'languages');

    const arrayResult = super.transform();
    this.result = isEmpty(arrayResult) ? this.result : { languages: arrayResult };

    this.source = originalSource;
    this.changes = _filter(originalChanges, (change: any) => change.path[0] !== 'languages');

    forEach(this.changes, (change: any) => {
      if (change.kind !== 'E' && change.kind !== 'N') return;
      const fieldName = last(change.path) as string;
      if (change.kind === 'E') {
        this.result[fieldName] = change.rhs;
      } else if (change.kind === 'N') {
        isObject(change.rhs) ? ngExtend(this.result, change.rhs) : (this.result[fieldName] = change.rhs);
      }
    });
    return this.result;
  }
}

class HomepageMixedDataTransformer extends MixedDataTransformer {
  override transform(): any {
    this.result = super.transform();
    if (has(this.result, 'languages')) {
      const languages: any[] = [];
      forEach(this.result.languages, (language: any) => {
        const languageCopy = stripHashKeys(_cloneDeep(language));
        const item: any = {};
        item[this.destinationKey] = languageCopy[this.destinationKey];
        delete languageCopy[this.destinationKey];
        item.data = languageCopy;
        languages.push(item);
      });
      this.result.languages = { item: languages };
    }
    return this.result;
  }
}

class AccountDataTransformer extends FlatDataTransformer {
  constructor(changes: any, source: any) {
    super(changes, source);
    this.mapper.push({ source: '@code', destination: 'code' });
    this.mapper.push({ source: '@date_format', destination: 'dateFormat' });
    this.mapper.push({ source: '@is_active', destination: 'isActive' });
    this.mapper.push({ source: '@is_account_v1_enabled', destination: 'isAccountV1Enabled' });
    this.mapper.push({ source: '@is_account_v2_enabled', destination: 'isAccountV2Enabled' });
    this.mapper.push({ source: '@is_account_v2_default', destination: 'isAccountV2Default' });
    this.mapper.push({ source: '@name', destination: 'name' });
    this.mapper.push({ source: '@url', destination: 'url' });
    this.mapper.push({ source: '@date_format', destination: 'dateFormat' });
  }
}

class PhotoContestIntegrationDataTransformer extends Transformer {
  override transform(): any {
    this.result = {
      isBookNowActive: this.source.isBookNowActive,
      isFooterActive: this.source.isFooterActive,
      codes: { deleteCodes: [], newCodes: [] },
    };

    forEach(this.changes, (change: any) => {
      if (change.kind == 'A') {
        if (change.item.kind == 'N') {
          this.result.codes.newCodes.push(change.item.rhs.code);
        }
        if (change.item.kind == 'D') {
          this.result.codes.deleteCodes.push(change.item.lhs.code);
        }
      } else if (change.kind == 'E' && last(change.path) === 'code') {
        this.result.codes.deleteCodes.push(change.lhs);
        this.result.codes.newCodes.push(change.rhs);
      }
    });
    const codeDiffs = union(
      difference(this.result.codes.deleteCodes, this.result.codes.newCodes),
      difference(this.result.codes.newCodes, this.result.codes.deleteCodes),
    );
    remove(this.result.codes.deleteCodes, (code: any) => !includes(codeDiffs, code));
    remove(this.result.codes.newCodes, (code: any) => !includes(codeDiffs, code));

    return this.result;
  }
}

function resolveFactory(type: number, changes: any, source: any): Transformer {
  switch (type) {
    case transformType.MultiLanguageData:
      return new MultiLanguageDataTransformer(changes, source);
    case transformType.ArrayData:
      return new ArrayDataTransformer(changes, source);
    case transformType.FlatData:
      return new FlatDataTransformer(changes, source);
    case transformType.BookingEngineData:
      return new BookingEngineDataTransformer(changes, source);
    case transformType.BookingEngineIntegrationData:
      return new IntegrationDataTransformer(changes, source);
    case transformType.NotificationData:
      return new NotificationDataTransformer(changes, source);
    case transformType.MixedData:
      return new MixedDataTransformer(changes, source);
    case transformType.PhotoContestPageIntegration:
      return new PhotoContestIntegrationDataTransformer(changes, source);
    case transformType.HomepageMixedData:
      return new HomepageMixedDataTransformer(changes, source);
    case transformType.AccountData:
      return new AccountDataTransformer(changes, source);
    default:
      throw 'Not supported density precision'; // sic — the v1 error string, kept verbatim
  }
}

/** v1 `changesTransformer(type, changes, source)` — deep-diff changes → the api payload. */
export function changesTransformer(type: number, changes: any, source: any): any {
  return resolveFactory(type, changes, source).transform();
}
